/**
 * 008-reporting-dashboards T018/T020 (FR-013, SC-005). Same shape as `StockMovement.tsx`/
 * `Variance.tsx`: a from/to-bounded, unpaged, client-exported report — period form driving
 * `searchParams`, fetch-on-submit-or-bookmarked-load, `ExportButton` fed rows mapped to plainly-keyed
 * objects in the exact header order (`flattenObject`'s `for...in` key-order hazard, see
 * `GstSummary.tsx`'s own comment).
 *
 * `difference`, `attribution`, `aggregate.net` and `aggregate.percentOfInvoiced` are the API's own
 * figures — this file contains no subtraction or division over `orderedValue`/`invoicedValue`/
 * `freightExcluded` (FR-013, SC-005; T018's own test greps this file's source for exactly that).
 *
 * Gated on `orders.read` (008-reporting-dashboards T037, contracts/screen-routes.md) — was
 * `orders.read` (the nearest code to the backend's real `orders.administer`, GAP-5) before T037
 * added a dedicated code for this feature's own report routes.
 */
import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Text, Button } from "rizzui";
import { WidgetCard, ErrorSurface, EmptyState, ExportButton, FieldError } from "@steelyard/theme";
import { DateField } from "../../components/date-field";
import { ListHeader } from "../../components/table/list-header";
import { ReportTable } from "../../components/table/table-view";
import { can } from "../../lib/permissions";
import { formatDate, formatMoney, sentenceCase, parseIsoDate, toIsoDate } from "../../lib/format";
import { getOrderToInvoiceReport, money, attributionLabel, type OrderToInvoiceReport } from "../../lib/services/ordering";
import type { AccountSummary, PermissionState } from "../../types";
import { orderToInvoiceColumns } from "./order-to-invoice-columns";

/** Formatting, not calculation — renders the API's own `percentOfInvoiced` fraction (constitution I), same as `warehouse.ts`'s `formatPercent`. `null` (empty denominator) renders nothing, never 0%. */
const PERCENT = new Intl.NumberFormat("en-IN", { style: "percent", maximumFractionDigits: 1 });
function formatPercentOfInvoiced(value: number | string | null): string | null {
  if (value === null) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? PERCENT.format(parsed) : null;
}


/*
 * The rows render through `ReportTable`, keyed on `producedAt` so each fetch starts from its first
 * page — paged in memory, not `PagedTable`, for the export-guard reason in `Variance.tsx`.
 */

export default function OrderToInvoiceReportPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<OrderToInvoiceReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);
  const [periodError, setPeriodError] = useState<string>();

  // Rules-of-hooks: called unconditionally, ahead of the permission-gate return below.
  useEffect(() => {
    if (from && to) run(from, to);
  }, [from, to]);

  if (!can(permissionState, "orders.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403, detail: "The order-to-invoice report is gated by orders.read, which your role does not hold." }} action="view the order-to-invoice report" />;
  }

  async function run(nextFrom: string, nextTo: string) {
    setLoading(true);
    setError(null);
    try {
      setReport(await getOrderToInvoiceReport({ from: nextFrom, to: nextTo }));
    } catch (err) {
      setError(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!fromDate || !toDate) return setPeriodError("Choose both a From and a To date.");
    setPeriodError(undefined);
    setSearchParams({ from: fromDate, to: toDate });
    run(fromDate, toDate);
  }

  const header = (
    <ListHeader
      title="Order-To-Invoice Variance"
      description="Every invoiced credit order’s ordered vs. invoiced value over a period, attributed to weight variance or short fulfilment — the API’s own figures, not recomputed here."
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Orders" }, { name: "Order-To-Invoice Variance" }]}
    >
        {report && !report.empty ? (
          <ExportButton
            data={report.rows.map((r) => ({
              Order: r.orderNumber,
              Ordered: r.orderedValue,
              Invoiced: r.invoicedValue,
              "Freight excluded": r.freightExcluded,
              Difference: r.difference,
              Attribution: attributionLabel(r.attribution),
              "Invoiced at": formatDate(r.invoicedAt),
            }))}
            header="Order,Ordered,Invoiced,Freight excluded,Difference,Attribution,Invoiced at"
            fileName={`order-to-invoice-${from}-to-${to}`}
          />
        ) : null}
    </ListHeader>
  );

  const periodForm = (
    <WidgetCard title="Period" rounded="lg" headerClassName="border-muted border-b pb-4">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4 pt-4">
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="From…"
          selected={parseIsoDate(fromDate || null)}
          maxDate={parseIsoDate(toDate || null) ?? undefined}
          onChange={(date) => setFromDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "From", clearable: Boolean(fromDate), onClear: () => setFromDate("") }}
        />
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="To…"
          selected={parseIsoDate(toDate || null)}
          minDate={parseIsoDate(fromDate || null) ?? undefined}
          onChange={(date) => setToDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "To", clearable: Boolean(toDate), onClear: () => setToDate("") }}
        />
        <Button type="submit" disabled={loading}>{loading ? "Running…" : "Run the report"}</Button>
        {periodError ? <div className="basis-full"><FieldError message={periodError} /></div> : null}
      </form>
    </WidgetCard>
  );

  if (!from || !to) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        {periodForm}
        <EmptyState title="Choose a period">Bounded to a range on purpose — 8 years of financial records sit behind this report.</EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      {periodForm}

      {error ? <ErrorSurface error={error} action="run the order-to-invoice report" /> : null}

      {report ? (
        report.empty ? (
          <WidgetCard title={<>{formatDate(from)} to {formatDate(to)}</>} rounded="lg" headerClassName="border-muted border-b">
            <EmptyState title="No invoiced orders in this period">
              {report.emptyReason ? sentenceCase(report.emptyReason) + "." : "No credit order was invoiced in the chosen range."}
            </EmptyState>
          </WidgetCard>
        ) : (
          <>
            <WidgetCard title={<>{formatDate(from)} to {formatDate(to)}</>} rounded="lg" headerClassName="border-muted border-b">
              <Text className="pt-4">
                {report.aggregate.orderCount} orders · net {formatMoney(money(report.aggregate.net))}
                {formatPercentOfInvoiced(report.aggregate.percentOfInvoiced) ? ` (${formatPercentOfInvoiced(report.aggregate.percentOfInvoiced)} of invoiced value)` : ""}
                {" · "}
                {report.excludedUninvoicedOrders} order{Number(report.excludedUninvoicedOrders) === 1 ? "" : "s"} excluded as not yet invoiced
                {" · "}
                {report.excludedB2cOrders} B2C order{Number(report.excludedB2cOrders) === 1 ? "" : "s"} excluded
              </Text>
            </WidgetCard>

            <ReportTable<OrderToInvoiceReport["rows"][number]>
              key={report.producedAt}
              title={<>{report.rows.length} {report.rows.length === 1 ? "row" : "rows"}</>}
              rows={report.rows}
              columns={orderToInvoiceColumns}
              getRowId={(row) => row.orderId}
              caption="Orders in the period, with their ordered and invoiced value, the API’s own difference and its attribution"
              searchPlaceholder="Search orders…"
            />
          </>
        )
      ) : null}
    </div>
  );
}
